// =============================================================================
// Le graphique « Puissances par poste » (D14, décisions v1.2 §B du 18/10/2026).
//
// Ce que le lecteur doit vivre (revue du 19/10/2026, N1, qui remplace les
// choix B4, B5 et B6 du 18/10) : poste par poste, sans clic, TOUJOURS deux
// barres couchées depuis zéro à la même échelle, la puissance actuelle
// (claire) puis la puissance recommandée (pleine), égales quand rien ne
// change ; la valeur de chaque barre écrite au bout, à l'extérieur ; un trait
// vertical qui traverse les deux barres à la puissance maximale atteinte, sa
// valeur écrite une fois au-dessus. La réserve (vert) et le dépassement
// (rouge) sont le REMPLISSAGE de la barre, jamais une étiquette chiffrée : la
// barre garde la longueur de la puissance souscrite, sa part au-delà du pic
// est verte ; un pic au-delà de la barre se dessine en rouge dans son
// prolongement, jusqu'au trait. Le tableau reste replié pour copier les
// chiffres.
//
// Ce fichier ne dessine rien : il dit QUOI dessiner (le modèle). La géométrie
// est dans `optim-turpe-v2.graphique.dessin.ts`. Règles :
// - une valeur absente reste `null` et n'a ni barre, ni trait, ni chiffre,
//   jamais 0 ;
// - mesure incomplète (courbe « très incomplète ») : aucune réserve ni aucun
//   dépassement dessiné, trait en pointillé (choix 9) ;
// - C5 : un seul poste « Toutes les heures », jamais « dépassement »,
//   « couvrir » ni « assumer » : le rouge s'y lit « risque de disjonction » ;
// - les lignes, la colonne de l'étude et la mention « relevée » viennent des
//   fonctions du tableau (`optim-turpe-v2.tableau.ts`) : le tableau replié et
//   le graphique disent exactement les mêmes valeurs.
// =============================================================================
import { formateurFr } from "@modules/courtage/utils/optim-turpe-v2.nombre"
import {
    estC5,
    getUnitePuissance,
} from "@modules/courtage/utils/optim-turpe-v2.helpers"
import { reserveTitreC5 } from "@modules/courtage/utils/optim-turpe-v2.recommandation"
import { lireRaccordement } from "@modules/courtage/utils/optim-turpe-v2.bornes"
import { lireChiffre } from "@modules/courtage/utils/optim-turpe-v2.chiffre"
import {
    LIBELLE_CLASSE_HORAIRE,
    LIBELLE_SCENARIO_ARBITRAGE,
} from "@modules/courtage/utils/optim-turpe-v2.libelles"
import {
    COLONNE_OPTION_CHIFFREE,
    LIBELLE_LIGNE_UNIQUE,
    colonneProposee,
    lignesTableauPlages,
    margeLisiblePct,
    recommandeeParPoste,
    segmentsTableauPlages,
} from "@modules/courtage/utils/optim-turpe-v2.tableau"
import type { OptimTurpeV2Etude } from "@modules/courtage/types/optim-turpe-v2"

/** Choix 3 : au-delà de 1,6 fois la plus grande valeur, le raccordement sort de l'échelle (sauf C5). */
export const RACCORDEMENT_HORS_ECHELLE = 1.6

/** L'échelle dépasse la plus grande valeur de 3 % (la maquette, variante A). */
export const MARGE_ECHELLE_GRAPHIQUE = 1.03

/** Au plus six graduations après zéro (greffe n° 10 : la règle `nice`). */
export const GRADUATIONS_MAX = 6

/** Ce que l'écart d'une barre au trait veut dire. */
export type SensEcart = "reserve" | "depassement" | "disjonction"

/** Une barre d'un poste : la puissance souscrite aujourd'hui ou la recommandée. */
export type BarreGraphique = {
    nature: "aujourdhui" | "propose"
    valeur: number
    /**
     * L'écart entre l'extrémité de la barre et le trait du pic : réserve (le
     * pic est sous la barre, remplissage vert), dépassement (au-dessus,
     * prolongement rouge), ou, en C5, risque de disjonction (rouge). Jamais
     * écrit à côté de la barre (N1) : il décide du remplissage et du texte
     * alternatif. `null` sans pic, sous une mesure incomplète, ou à écart nul.
     */
    ecart: { sens: SensEcart; valeur: number } | null
    /**
     * Choix 1 : la portion bleu clair de la barre pleine, du pic au pic majoré
     * de la marge de sécurité (bornée à la barre). `null` sans marge lisible.
     */
    marge: { de: number; a: number } | null
    /** Le texte alternatif de la barre, lu par un lecteur d'écran. */
    texte: string
}

/** Un poste du graphique. */
export type PosteGraphique = {
    /** Code de la plage (`HPH`…) ou `unique` en C5. */
    cle: string
    /** Le poste en clair, majuscule initiale (« Heures pleines de saison haute »). */
    nom: string
    /** Le code visible (`HPH`), `null` en C5. */
    code: string | null
    pic: number | null
    barres: BarreGraphique[]
    /** Choix 4, cas 8 : le repère creux à la puissance recommandée, `null` sinon. */
    repereConseil: number | null
    /** Choix 2 : la mention courte « relevée au niveau du poste précédent ». */
    relevee: boolean
}

/** L'info-bulle de la mention « relevée » (I39), selon le segment. */
export { infoRelevee } from "@modules/courtage/utils/optim-turpe-v2.tableau"

/** Le raccordement tel que le graphique le montre. */
export type RaccordementDuGraphique = {
    valeur: number
    unite: string
    /** Choix 3 : écarté par le moteur (dépassé par les mesures), dit en orange. */
    ecarte: boolean
    /** Faux : hors échelle, une mention en haut à droite au lieu du pointillé. */
    dansEchelle: boolean
}

/** Un signe de la légende et son texte. */
export type SigneLegende =
    | "aujourdhui"
    | "propose"
    | "repere"
    | "pic"
    | "pic_pointille"
    | "reserve"
    | "marge"
    | "depassement"
    | "raccordement"
    | "raccordement_ecarte"

export type ElementLegendeGraphique = { signe: SigneLegende; texte: string }

/** Le modèle complet du graphique. */
export type ModeleGraphique = {
    unite: string
    c5: boolean
    /** Choix 9 : la courbe est très incomplète, le pic n'est pas établi. */
    incomplete: boolean
    /**
     * La barre pleine : « Recommandé » (y compris inchangée), « Vos
     * puissances », « Option chiffrée », ou `null` quand rien n'est recommandé.
     */
    colonne: string | null
    postes: PosteGraphique[]
    raccordement: RaccordementDuGraphique | null
    /** Le maximum de l'axe et son pas ; `null` quand rien n'est dessinable. */
    echelle: { max: number; pas: number; graduations: number[] } | null
    legende: ElementLegendeGraphique[]
    /** « Puissances en kVA · marge de sécurité 5 % ». */
    meta: string
    /** Le pied : une phrase au plus (contrat v1.2), dans une liste vide ou d'un élément. */
    pied: string[]
}

/**
 * Ce que la page sait et que l'étude seule ne dit pas : le scénario affiché
 * n'est pas le conseil (`optionChiffree`), et l'autre étude de la paire.
 */
export type ContexteGraphique = {
    optionChiffree?: boolean
    /** L'étude de l'autre scénario de la paire, quand elle existe. */
    autre?: OptimTurpeV2Etude | null
    /** Le scénario de l'étude affichée : « sans » (Couvrir) ou « avec » (Assumer). */
    scenario?: "sans" | "avec" | null
}

const NOMBRE_UNE_DECIMALE = formateurFr({
    maximumFractionDigits: 1,
})

/** « 67,8 » : une décimale au plus (F18). */
export function nombreGraphique(v: number): string {
    return NOMBRE_UNE_DECIMALE.format(v)
}

function fini(v: unknown): number | null {
    return typeof v === "number" && Number.isFinite(v) ? v : null
}

function majuscule(texte: string): string {
    return texte ? texte.charAt(0).toUpperCase() + texte.slice(1) : texte
}

function arrondi1(v: number): number {
    return Math.round(v * 10) / 10
}

/** Choix 9 : la courbe « très incomplète » ne permet d'établir aucun pic. */
export function mesureIncomplete(etude: OptimTurpeV2Etude): boolean {
    return etude.qualite_donnees?.niveau === "faible"
}

/** Les puissances de la structure chiffrée d'une étude, par poste (`unique` en C5). */
function puissancesDe(
    etude: OptimTurpeV2Etude | null | undefined
): Record<string, number> {
    const p =
        etude?.meilleure?.structure_tarifaire?.puissance_souscrite_soutirage
    if (!p) return {}
    const sortie: Record<string, number> = {}
    const unique = fini(p.puissance)
    if (unique !== null) sortie.unique = unique
    for (const [cle, v] of Object.entries(p.denivele ?? {})) {
        const lue = fini(v)
        if (lue !== null) sortie[cle] = lue
    }
    return sortie
}

/**
 * L'écart d'une barre au trait. C5 : une puissance souscrite sous le pic se
 * dit « risque de disjonction » (vocabulaire C5, jamais « dépassement ») ;
 * elle se dessine en rouge comme un dépassement (N1, C5 : même forme).
 */
function ecartDe(
    valeur: number,
    pic: number | null,
    c5: boolean,
    incomplete: boolean
): BarreGraphique["ecart"] {
    if (pic === null || incomplete) return null
    const diff = arrondi1(valeur - pic)
    if (diff === 0) return null
    if (diff > 0) return { sens: "reserve", valeur: diff }
    return { sens: c5 ? "disjonction" : "depassement", valeur: -diff }
}

/** Le texte alternatif d'une barre : « aujourd'hui 66 kVA, réserve 3,6 kVA ». */
function texteBarre(
    nomBarre: string,
    valeur: number,
    ecart: BarreGraphique["ecart"],
    unite: string
): string {
    const v = (x: number) => `${nombreGraphique(x)}\u00a0${unite}`
    const base = `${nomBarre} ${v(valeur)}`
    if (!ecart) return base
    if (ecart.sens === "reserve") return `${base}, réserve ${v(ecart.valeur)}`
    if (ecart.sens === "depassement")
        return `${base}, dépassement ${v(ecart.valeur)}`
    return `${base}, risque de disjonction, ${v(ecart.valeur)} au-dessus de la puissance souscrite`
}

/** Le nom de la barre pleine, en minuscules, pour la légende et les textes. */
function nomBarrePleine(colonne: string | null): string {
    if (colonne === "Vos puissances") return "vos puissances"
    if (colonne === COLONNE_OPTION_CHIFFREE) return "scénario retenu"
    return "puissance recommandée"
}

/**
 * Le pas de l'axe : 1, 2, 2,5 ou 5 fois une puissance de dix, celui qui donne
 * le maximum le plus serré en six graduations au plus (à égalité, le plus
 * petit pas). 67,8 donne 0, 25, 50, 75 ; 105 donne 0 à 120 par 20.
 */
export function echelleGraphique(
    max: number | null
): ModeleGraphique["echelle"] {
    if (max === null || !(max > 0)) return null
    const cible = max * MARGE_ECHELLE_GRAPHIQUE
    const base = Math.pow(10, Math.floor(Math.log10(cible / GRADUATIONS_MAX)))
    const candidats = [1, 2, 2.5, 5, 10, 20, 25, 50]
        .map((f) => f * base)
        .filter((p) => Math.ceil(cible / p - 1e-9) <= GRADUATIONS_MAX)
        .map((p) => ({ pas: p, max: Math.ceil(cible / p - 1e-9) * p }))
        .sort((a, b) => a.max - b.max || a.pas - b.pas)
    const { pas, max: haut } = candidats[0] ?? {
        pas: base * 100,
        max: base * 100,
    }
    const n = Math.round(haut / pas)
    const graduations = Array.from(
        { length: n + 1 },
        (_, i) => Math.round(i * pas * 100) / 100
    )
    return { max: graduations[graduations.length - 1], pas, graduations }
}

/**
 * Le raccordement du graphique (choix 3). Hors échelle au-delà de 1,6 fois la
 * plus grande valeur dessinée, sauf C5 où il reste tracé (c'est le plafond du
 * segment). Une unité qui diffère de celle des puissances ne se trace jamais.
 */
export function raccordementGraphique(
    etude: OptimTurpeV2Etude,
    plusGrande: number | null
): RaccordementDuGraphique | null {
    const lu = lireRaccordement(etude)
    if (!lu || lu.valeur === null || lu.valeur <= 0 || plusGrande === null)
        return null
    const uniteTableau = getUnitePuissance(etude)
    const unite = lu.unite ?? uniteTableau
    const dansEchelle =
        unite === uniteTableau &&
        (estC5(etude) || lu.valeur <= plusGrande * RACCORDEMENT_HORS_ECHELLE)
    return {
        valeur: lu.valeur,
        unite,
        ecarte: lu.statut === "ecartee",
        dansEchelle,
    }
}

/** Les postes du graphique, dans l'ordre du tableau. */
function postesDuGraphique(
    etude: OptimTurpeV2Etude,
    contexte: ContexteGraphique,
    incomplete: boolean
): {
    postes: PosteGraphique[]
    inchangee: boolean
    colonne: string | null
} {
    const options = { optionChiffree: contexte.optionChiffree }
    const c5 = estC5(etude)
    const unite = getUnitePuissance(etude)
    const lignes = lignesTableauPlages(etude, options)
    const relevees = new Set(
        segmentsTableauPlages(etude, options)
            .filter((l) => l.relevee)
            .map((l) => l.cle)
    )
    const margePct = margeLisiblePct(etude)
    const conseil =
        contexte.optionChiffree && contexte.autre
            ? puissancesDe(contexte.autre)
            : {}
    // N1 (19/10/2026) : la seconde barre est TOUJOURS là, égale à la
    // première quand rien ne change ; le pied le dit aussi.
    const recommandee = recommandeeParPoste(etude, options)
    const colonne = recommandee?.colonne ?? null
    const pleine = nomBarrePleine(colonne)

    const postes = lignes.map((l): PosteGraphique => {
        const barres: BarreGraphique[] = []
        if (l.aujourdhui !== null) {
            const ecart = ecartDe(l.aujourdhui, l.pointe, c5, incomplete)
            barres.push({
                nature: "aujourdhui",
                valeur: l.aujourdhui,
                ecart,
                marge: null,
                texte: texteBarre("aujourd'hui", l.aujourdhui, ecart, unite),
            })
        }
        const propose = recommandee?.valeurs[l.cle] ?? null
        if (propose !== null) {
            const ecart = ecartDe(propose, l.pointe, c5, incomplete)
            const marge =
                margePct !== null &&
                !incomplete &&
                l.pointe !== null &&
                propose > l.pointe
                    ? {
                          de: l.pointe,
                          a: Math.min(
                              propose,
                              Math.round(
                                  l.pointe * (1 + margePct / 100) * 100
                              ) / 100
                          ),
                      }
                    : null
            barres.push({
                nature: "propose",
                valeur: propose,
                ecart,
                marge,
                texte: texteBarre(pleine, propose, ecart, unite),
            })
        }
        const code = l.cle === "unique" ? null : l.cle
        const repere = conseil[l.cle]
        return {
            cle: l.cle,
            nom:
                l.cle === "unique"
                    ? LIBELLE_LIGNE_UNIQUE
                    : majuscule(LIBELLE_CLASSE_HORAIRE[l.cle] ?? l.cle),
            code,
            pic: l.pointe,
            barres,
            repereConseil:
                repere !== undefined && repere !== propose ? repere : null,
            relevee: relevees.has(l.cle),
        }
    })
    return { postes, inchangee: recommandee?.inchangee === true, colonne }
}

/** La légende en une ligne (N1 du 19/10/2026), limitée aux signes dessinés. */
function legendeDuGraphique(
    m: Omit<ModeleGraphique, "legende" | "meta" | "pied">
): ElementLegendeGraphique[] {
    const barres = m.postes.flatMap((p) => p.barres)
    const elements: ElementLegendeGraphique[] = []
    if (barres.some((b) => b.nature === "aujourdhui"))
        elements.push({
            signe: "aujourdhui",
            texte: "barre claire : puissance actuelle",
        })
    if (barres.some((b) => b.nature === "propose"))
        elements.push({
            signe: "propose",
            texte: `barre pleine : ${nomBarrePleine(m.colonne)}`,
        })
    if (m.postes.some((p) => p.repereConseil !== null))
        elements.push({
            signe: "repere",
            texte: "repère creux : puissance recommandée",
        })
    if (m.postes.some((p) => p.pic !== null))
        elements.push(
            m.incomplete
                ? {
                      signe: "pic_pointille",
                      texte: "trait : puissance maximale atteinte, non établie",
                  }
                : { signe: "pic", texte: "trait : puissance maximale atteinte" }
        )
    if (barres.some((b) => b.ecart?.sens === "reserve"))
        elements.push({ signe: "reserve", texte: "vert : réserve" })
    // Décision (b) du 19/10/2026 (DEV-2429, C14 du contrat v1.4) : la portion
    // bleu clair est nommée, seulement quand une marge non nulle est dessinée.
    if (barres.some((b) => b.marge !== null && b.marge.a > b.marge.de))
        elements.push({
            signe: "marge",
            texte: "bleu clair : marge de sécurité",
        })
    if (barres.some((b) => b.ecart?.sens === "depassement"))
        elements.push({ signe: "depassement", texte: "rouge : dépassement" })
    else if (barres.some((b) => b.ecart?.sens === "disjonction"))
        elements.push({
            signe: "depassement",
            texte: "rouge : risque de disjonction",
        })
    if (m.raccordement?.dansEchelle)
        elements.push(
            m.raccordement.ecarte
                ? {
                      signe: "raccordement_ecarte",
                      texte: "pointillé orange : raccordement écarté",
                  }
                : { signe: "raccordement", texte: "pointillé : raccordement" }
        )
    if (elements.length)
        elements[0] = { ...elements[0], texte: majuscule(elements[0].texte) }
    return elements
}

/** La liste des valeurs d'un scénario, « 68 (PTE), 83 (HPH)… » : le code entre parenthèses. */
function valeursDuScenario(
    postes: PosteGraphique[],
    puissances: Record<string, number>
): string | null {
    const parties = postes
        .filter((p) => puissances[p.cle] !== undefined)
        .map((p) =>
            p.code
                ? `${nombreGraphique(puissances[p.cle])} (${p.code})`
                : nombreGraphique(puissances[p.cle])
        )
    return parties.length ? parties.join(", ") : null
}

/** Les phrases du pied (choix 4, 9, 11 ; raccordement écarté). */
function piedDuGraphique(
    etude: OptimTurpeV2Etude,
    contexte: ContexteGraphique,
    m: Omit<ModeleGraphique, "legende" | "meta" | "pied">,
    inchangee: boolean,
    colonneServie: string | null
): string[] {
    const pied: string[] = []
    if (m.incomplete && m.postes.some((p) => p.pic !== null))
        pied.push(
            "Courbe de charge incomplète sur la période : la puissance maximale atteinte n'est pas établie, aucune réserve n'est chiffrée."
        )
    // DEV-2423 : sur un C5 avec la réserve servie, la puissance recommandée
    // est celle de la réserve (11 kVA), jamais « inchangée » ; `inchangee`
    // vient de `recommandeeParPoste`, qui le sait.
    const reserveC5Servie =
        m.c5 &&
        !lireChiffre(etude).estScenarioLibre &&
        reserveTitreC5(etude) !== null
    if (!reserveC5Servie && inchangee)
        pied.push("Puissance recommandée : inchangée.")
    // Choix 4 : au cas « deux scénarios », l'autre est cité en pied, jamais
    // dessiné ; au cas 8, le retenu est dit, le conseil est le repère creux.
    if (!m.c5 && contexte.autre && contexte.scenario) {
        const autreScenario = contexte.scenario === "sans" ? "avec" : "sans"
        if (contexte.optionChiffree) {
            pied.push(
                `Scénario retenu : ${LIBELLE_SCENARIO_ARBITRAGE[contexte.scenario]}, à la place du scénario recommandé.`
            )
        } else if (colonneServie !== null) {
            const autre = puissancesDe(contexte.autre)
            const actuel = puissancesDe(etude)
            const differe = Object.keys(autre).some(
                (k) => autre[k] !== actuel[k]
            )
            const valeurs = valeursDuScenario(m.postes, autre)
            if (differe && valeurs)
                pied.push(
                    `Autre scénario, ${LIBELLE_SCENARIO_ARBITRAGE[autreScenario]} : ${valeurs}.`
                )
        }
    }
    if (m.raccordement?.ecarte)
        pied.push(
            "Raccordement écarté : la puissance maximale atteinte dépasse la valeur renseignée, elle n'a pas borné l'étude."
        )
    // Contrat v1.2, « Pied de carte » : une phrase, la première par ordre
    // d'importance (le raccordement écarté reste dit en orange sur l'axe).
    return pied.slice(0, 1)
}

/**
 * Le modèle du graphique d'une étude. Rend un modèle sans poste quand aucune
 * puissance n'est lisible : le composant ne dessine alors rien.
 */
export function modeleGraphique(
    etude: OptimTurpeV2Etude,
    contexte: ContexteGraphique = {}
): ModeleGraphique {
    const c5 = estC5(etude)
    const unite = getUnitePuissance(etude)
    const incomplete = mesureIncomplete(etude)
    const colonneServie = colonneProposee(etude, {
        optionChiffree: contexte.optionChiffree,
    })
    const { postes, inchangee, colonne } = postesDuGraphique(
        etude,
        contexte,
        incomplete
    )
    const valeurs = postes.flatMap((p) =>
        [...p.barres.map((b) => b.valeur), p.pic, p.repereConseil].filter(
            (v): v is number => v !== null && v > 0
        )
    )
    const plusGrande = valeurs.length ? Math.max(...valeurs) : null
    const raccordement = raccordementGraphique(etude, plusGrande)
    const echelle = echelleGraphique(
        plusGrande === null
            ? null
            : Math.max(
                  plusGrande,
                  raccordement?.dansEchelle ? raccordement.valeur : 0
              )
    )
    const base = {
        unite,
        c5,
        incomplete,
        colonne,
        postes,
        raccordement,
        echelle,
    }
    const margePct = margeLisiblePct(etude)
    const meta = [
        `Puissances en ${unite}`,
        ...(margePct !== null &&
        postes.some((p) => p.barres.some((b) => b.marge))
            ? [`marge de sécurité ${nombreGraphique(margePct)}\u00a0%`]
            : []),
    ].join(" · ")
    return {
        ...base,
        legende: legendeDuGraphique(base),
        meta,
        pied: piedDuGraphique(etude, contexte, base, inchangee, colonneServie),
    }
}
